import { type ProductTaxonomyType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, unref, type Ref } from 'vue';

import { findAssortmentTypologies } from '../../filter.query';

import { type FiltersTreeType } from '#composables/kpisPerimeter';
import { filterToTreeViewItemWithDefaultValue } from '#domain/core/filter/filter.mapper';

interface UseAssortmentTypologyDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
}

export function useAssortmentTypologiesData({
  debouncedSearch,
  enabled,
  checkedItemsState,
  taxonomy
}: UseAssortmentTypologyDataProps) {
  const {
    data: assortmentTypologyData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [findAssortmentTypologies.name, debouncedSearch, taxonomy],
    queryFn: async () => findAssortmentTypologies({ taxonomy: unref(taxonomy) }),
    select: (value) => (value.status === 'success' ? value.data : undefined)
  });

  const itemsAssortmentTypologies = computed(() => {
    if (!assortmentTypologyData.value) {
      return undefined;
    }

    const assortmentTypologyFiltered = assortmentTypologyData.value?.children?.filter(
      (child) =>
        child.label.includes(debouncedSearch.value) ||
        checkedItemsState.value.assortmentTypologyFilters[child.nodeId]?.state === 'checked'
    );

    if (!assortmentTypologyFiltered) {
      return undefined;
    }

    return filterToTreeViewItemWithDefaultValue({
      filter: assortmentTypologyData.value,
      defaultFilters: assortmentTypologyData.value,
      selectedFilters: checkedItemsState.value.assortmentTypologyFilters
    });
  });

  const isLoadingAssortmentTypology = computed(() => {
    // on tanstack query v4, there is a [known issue](https://github.com/TanStack/query/issues/3975) that
    // isLoading and isStale stay true if `enabled` is false.
    return isLoading.value && isFetching.value;
  });

  return { itemsAssortmentTypology: itemsAssortmentTypologies, isLoadingAssortmentTypology };
}
